import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Header from './Header';
import api from '../services/api';
import './CheckoutSuccess.css';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchSession = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/api/stripe/checkout-session/${sessionId}`);
        const session = response.data.session;
        setSessionData(session);

        // If checkout has a subscription and is paid/complete, sync it to the user (works when webhook hasn't run yet, e.g. local testing)
        const isPaid = session && session.subscription && (session.payment_status === 'paid' || session.status === 'complete');
        if (isPaid) {
          try {
            const syncRes = await api.post('/api/stripe/sync-subscription-from-session', { sessionId });
            if (syncRes.data && syncRes.data.updated) {
              toast.success('Subscription linked to your account. View it on your Dashboard.');
            }
          } catch (syncErr) {
            const msg = syncErr.response?.data?.message || syncErr.response?.data?.error || syncErr.message;
            const status = syncErr.response?.status;
            console.warn('Sync subscription failed:', status, msg, syncErr.response?.data);
            if (status === 401) {
              toast.error('Please log in and refresh this page to link your subscription to your account.');
            } else if (status === 403) {
              toast.error('This checkout was made with a different account. Log in with the email you used at checkout.');
            } else if (status === 500 && msg && msg.includes('column')) {
              toast.error('Server database needs subscription columns. Contact support or run database/add_subscription_fields.sql.');
            } else {
              toast.error(msg || 'Could not link subscription. Try your Dashboard or contact support.');
            }
          }
        }
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId]);

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #0f172a 100%)'}}>
      <Header />
      
      <main className="checkout-success-container">
        <div className="checkout-success-card">
          <div className="checkout-success-icon">
            <CheckCircle className="h-16 w-16" style={{color: '#10b981'}} />
          </div>
          
          <h1 className="checkout-success-title">Payment Successful!</h1>

          {loading && sessionId ? (
            <p className="checkout-success-message">Loading session details...</p>
          ) : (
          <>
            <p className="checkout-success-message">
              Thank you for your subscription. Your account has been upgraded and you now have access to all premium features.
            </p>
            <p className="checkout-success-message" style={{ marginTop: '0.5rem', fontSize: '0.95rem', color: '#94a3b8' }}>
              View your current subscription anytime under <strong style={{ color: '#e2e8f0' }}>Dashboard → Subscriptions</strong>.
            </p>
          </>
          )}

          {sessionData && (
            <div className="checkout-success-details">
              <p className="checkout-success-detail-item">
                <span className="checkout-success-label">Name:</span>
                <span className="checkout-success-value">
                  {sessionData.customer_details?.name || sessionData.customer_name || '—'}
                </span>
              </p>
              <p className="checkout-success-detail-item">
                <span className="checkout-success-label">Email:</span>
                <span className="checkout-success-value">{sessionData.customer_email || '—'}</span>
              </p>
              <p className="checkout-success-detail-item">
                <span className="checkout-success-label">Subscription:</span>
                <span className="checkout-success-value">
                  {sessionData.line_items?.data?.[0]?.price?.product?.name ||
                    (sessionData.subscription ? 'Active' : '—')}
                </span>
              </p>
            </div>
          )}

          <div className="checkout-success-actions">
            <button 
              onClick={() => navigate('/dashboard')}
              className="checkout-success-button"
            >
              Go to Dashboard
            </button>
            <button 
              onClick={() => navigate('/')}
              className="checkout-success-button checkout-success-button-outline"
            >
              Back to Home
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutSuccess;

